import type { ExternalRegisterAlertType } from "@/components/externalregister/types";
import i18n from "@/i18n";

import type {
  ExternalRegisterEntryRecord,
  ExternalRegisterMaintenanceEntry,
  ExternalRegisterSettings,
} from "./externalRegisterAdminTypes";
import { EXTERNAL_REGISTER_ADMIN_ALERT_TYPES } from "./externalRegisterAdminTypes";

export const EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS = {
  surname: 120,
  givenName: 120,
  hospital: 160,
  dateListed: 10,
  sex: 40,
  dob: 10,
  umrn: 80,
  comments: 2000,
} as const;

export const EXTERNAL_REGISTER_ALIAS_LIMITS = {
  maxCount: 100,
  maxLength: 120,
} as const;

export type ExternalRegisterEntryFieldErrors = Partial<Record<keyof ExternalRegisterEntryRecord, string>>;

export const emptyEntryRecord: ExternalRegisterEntryRecord = {
  surname: "",
  givenName: "",
  type: "SECURITY_PLAN",
  hospital: "",
  dateListed: "",
  aliases: [],
  sex: "",
  dob: "",
  umrn: "",
  comments: "",
};

const DEFAULT_ALERT_TYPE_SETTINGS_CONFIG: Record<ExternalRegisterAlertType, { labelKey: string; defaultLabel: string; color: string }> = {
  SECURITY_PLAN: { labelKey: "externalRegister.admin.helpers.alertTypeDefaults.securityPlan", defaultLabel: "Security Plan", color: "#000000" },
  MANAGEMENT_PLAN: { labelKey: "externalRegister.admin.helpers.alertTypeDefaults.managementPlan", defaultLabel: "Management Plan", color: "#64748B" },
  FAST_TRACK_MEDICAL: { labelKey: "externalRegister.admin.helpers.alertTypeDefaults.fastTrackMedical", defaultLabel: "Fast Track - Medical", color: "#64748B" },
  CHILD_PROTECTION: { labelKey: "externalRegister.admin.helpers.alertTypeDefaults.childProtectionRegister", defaultLabel: "Child Protection Register", color: "#64748B" },
  OTHER: { labelKey: "externalRegister.admin.helpers.alertTypeDefaults.other", defaultLabel: "Other", color: "#64748B" },
};

export const DEFAULT_ALERT_TYPE_SETTINGS: Record<ExternalRegisterAlertType, { label: string; color: string }> = Object.fromEntries(
  EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.map((type) => [
    type,
    {
      label: i18n.t(DEFAULT_ALERT_TYPE_SETTINGS_CONFIG[type].labelKey, { defaultValue: DEFAULT_ALERT_TYPE_SETTINGS_CONFIG[type].defaultLabel }),
      color: DEFAULT_ALERT_TYPE_SETTINGS_CONFIG[type].color,
    },
  ]),
) as Record<ExternalRegisterAlertType, { label: string; color: string }>;

export const getDefaultAlertTypeLabel = (type: ExternalRegisterAlertType): string => {
  const setting = DEFAULT_ALERT_TYPE_SETTINGS_CONFIG[type];
  return i18n.t(setting.labelKey, { defaultValue: setting.defaultLabel });
};

export const getDefaultAlertTypeColor = (type: ExternalRegisterAlertType): string => {
  return DEFAULT_ALERT_TYPE_SETTINGS_CONFIG[type].color;
};

export const getAlertTypeChipColor = (
  type: ExternalRegisterAlertType,
  settings: ExternalRegisterSettings | null,
): string => {
  return settings?.alertTypes?.[type]?.color?.trim() || getDefaultAlertTypeColor(type);
};

export const getPatientSummary = (entry: ExternalRegisterMaintenanceEntry): string => {
  return [
    [entry.givenName, entry.surname].filter(Boolean).join(" "),
    entry.umrn ? `${i18n.t("externalRegister.admin.helpers.summary.umrnPrefix", { defaultValue: "UMRN " })}${entry.umrn}` : undefined,
    entry.dob ? `${i18n.t("externalRegister.admin.helpers.summary.dobPrefix", { defaultValue: "DOB " })}${entry.dob}` : undefined,
  ].filter(Boolean).join(i18n.t("externalRegister.admin.helpers.summary.separator", { defaultValue: " · " }));
};

export const getExternalRegisterIdentity = (entry: ExternalRegisterMaintenanceEntry): string => {
  return getPatientSummary(entry) || i18n.t("externalRegister.admin.helpers.fallbacks.identityUnavailable", { defaultValue: "Register identity unavailable" });
};

export const getLinkedPatientName = (entry: ExternalRegisterMaintenanceEntry): string => {
  if (isNeverLinkedEntry(entry)) return i18n.t("externalRegister.admin.helpers.fallbacks.neverLinked", { defaultValue: "Never linked" });
  return entry.patientName || entry.linkedPatientId || entry.patientId || i18n.t("externalRegister.admin.helpers.fallbacks.noLinkedPatient", { defaultValue: "No linked patient" });
};

export const isNeverLinkedEntry = (entry: ExternalRegisterMaintenanceEntry): boolean => {
  return entry.linkStatus === "unlinked"
    && !entry.patientId
    && !entry.linkedPatientId
    && !entry.previousLinkedPatientId
    && !entry.unlinkedAt
    && !(entry.history ?? []).some((event) => event.action === "linked" || event.action === "unlinked");
};

export const normalizeExternalRegisterAliases = (aliases: readonly string[] = []) => {
  const seen = new Set<string>();
  return aliases.map((alias) => alias.trim()).filter((alias) => {
    const normalized = alias.toLowerCase();
    if (!alias || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

export const parseExternalRegisterAliases = (value: string) => normalizeExternalRegisterAliases(value.split(/[|,]/));

export const toEntryRecord = (entry: ExternalRegisterMaintenanceEntry): ExternalRegisterEntryRecord => ({
  surname: entry.surname ?? "",
  givenName: entry.givenName ?? "",
  type: entry.type,
  hospital: entry.hospital ?? "",
  dateListed: entry.dateListed ?? "",
  aliases: entry.aliases ?? [],
  sex: entry.sex ?? "",
  dob: entry.dob ?? "",
  umrn: entry.umrn ?? "",
  comments: entry.comments ?? "",
});

export const trimEntryRecord = (record: ExternalRegisterEntryRecord): ExternalRegisterEntryRecord => ({
  surname: record.surname.trim(),
  givenName: record.givenName.trim(),
  type: record.type,
  hospital: record.hospital.trim(),
  dateListed: record.dateListed.trim(),
  aliases: normalizeExternalRegisterAliases(record.aliases),
  ...(record.sex?.trim() ? { sex: record.sex.trim() } : {}),
  ...(record.dob?.trim() ? { dob: record.dob.trim() } : {}),
  ...(record.umrn?.trim() ? { umrn: record.umrn.trim() } : {}),
  ...(record.comments?.trim() ? { comments: record.comments.trim() } : {}),
});

export const isEntryRecordValid = (record: ExternalRegisterEntryRecord): boolean => {
  return Object.keys(getEntryRecordValidationErrors(record)).length === 0;
};

const isValidDate = (value: string): boolean => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

const validateStringLength = (
  errors: ExternalRegisterEntryFieldErrors,
  field: keyof typeof EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS,
  value: string | undefined,
) => {
  const maxLength = EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS[field];
  if (value && value.length > maxLength) {
    errors[field] = i18n.t("common.validation.maxLength", { length: maxLength });
  }
};

export const getEntryRecordValidationErrors = (record: ExternalRegisterEntryRecord): ExternalRegisterEntryFieldErrors => {
  const errors: ExternalRegisterEntryFieldErrors = {};

  (["surname", "givenName", "hospital", "dateListed"] as const).forEach((field) => {
    if (!record[field]?.trim()) {
      errors[field] = i18n.t("common.validation.required");
    }
  });

  if (!EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.includes(record.type)) {
    errors.type = i18n.t("common.validation.required");
  }

  (Object.keys(EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS) as Array<keyof typeof EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS>)
    .forEach((field) => validateStringLength(errors, field, record[field]));

  const aliases = normalizeExternalRegisterAliases(record.aliases);
  if (aliases.length > EXTERNAL_REGISTER_ALIAS_LIMITS.maxCount) {
    errors.aliases = i18n.t("common.validation.maxCount", { count: EXTERNAL_REGISTER_ALIAS_LIMITS.maxCount });
  } else if (aliases.some((alias) => alias.length > EXTERNAL_REGISTER_ALIAS_LIMITS.maxLength)) {
    errors.aliases = i18n.t("common.validation.maxLength", { length: EXTERNAL_REGISTER_ALIAS_LIMITS.maxLength });
  }

  if (record.dateListed?.trim() && !isValidDate(record.dateListed.trim())) {
    errors.dateListed = i18n.t("common.validation.dateFormat");
  }

  if (record.dob?.trim() && !isValidDate(record.dob.trim())) {
    errors.dob = i18n.t("common.validation.dateFormat");
  }

  return errors;
};
